import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { ANIMALS, TERRAIN, NIGHT, AUDIO } from '../config';
import { terrainHeight } from '../engine/terrain';
import { biomeAt, Biome } from '../engine/biome';
import { applyInteriorDark } from '../engine/interior';
import type { ItemId } from './inventory';
import type { Box, BoxPool } from '../engine/collision';

// Wild animals: deer, chicken, cow (skittish -- flee the player) and boar (territorial -- charges, gores, and
// makes you bleed). Each wanders, is seated on the terrain, and stays out of deep water. Shoot or club them to
// kill; death drops RAW meat (cook it -- raw poisons). Killed animals respawn after a while so herds persist.
// Drawn with one low-poly InstancedMesh per species (matrices rewritten each frame since they move); AI only
// ticks for animals near the player. Combat hooks: hitTest (bullets) + nearestLive/damage (melee), colliders
// for the big ones (cow/boar) so you cannot walk through them.

export type Species = 'deer' | 'chicken' | 'boar' | 'cow';
interface SpeciesDef {
  hp: number; speed: number; flee: number; meat: ItemId; amount: number;
  fleeRange: number; hostile: boolean; big: boolean; scale: number; radius: number;
}
const SPECIES: Record<Species, SpeciesDef> = {
  deer: { hp: 45, speed: 2.2, flee: 8.5, meat: 'meat_deer_raw', amount: 3, fleeRange: 17, hostile: false, big: false, scale: 1.0, radius: 0.7 },
  chicken: { hp: 12, speed: 1.6, flee: 5.0, meat: 'meat_chicken_raw', amount: 1, fleeRange: 9, hostile: false, big: false, scale: 0.68, radius: 0.45 },
  boar: { hp: 70, speed: 1.8, flee: 0, meat: 'meat_boar_raw', amount: 4, fleeRange: 0, hostile: true, big: true, scale: 1.05, radius: 0.7 },
  cow: { hp: 95, speed: 1.1, flee: 4.5, meat: 'meat_cow_raw', amount: 6, fleeRange: 12, hostile: false, big: true, scale: 1.3, radius: 0.85 },
};

export interface Animal {
  species: Species;
  pos: THREE.Vector3; vel: THREE.Vector3; yaw: number; hp: number; alive: boolean;
  wanderT: number; wanderYaw: number; attackCd: number; respawnAt: number;
  sightFade: number; sightTarget: number; // SIGHT LOS: eased visibility (0 = character can't see it -> hidden)
  strideDist: number; // metres walked since the last footfall (AUDIO: distance-based cadence, mirrors Player.footfallCount)
  netX: number; netZ: number; netYaw: number; // MP follower mode: the host's last snapshot pos/yaw we lerp toward
  retreatUntil: number; // boar hit-and-run: wall-clock secs until it stops fleeing + can re-aggro (0 = not retreating)
}

function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => { a |= 0; a = (a + 0x6d2b79f5) | 0; let t = Math.imul(a ^ (a >>> 15), 1 | a); t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t; return ((t ^ (t >>> 14)) >>> 0) / 4294967296; };
}

// One animal's saved state, index-aligned with the deterministic spawnAll() order (same seed + terrain
// -> same herd list every boot; the species field is a guard against a changed roster, not a key).
// `rem` = seconds until a dead animal respawns; absent = alive. Position/hp carry over so a wounded
// deer you chased does not reset to a fresh one across a reload.
export interface SavedAnimal { sp: Species; x: number; z: number; hp: number; rem?: number; }

export class AnimalField {
  readonly group = new THREE.Group();
  private readonly meshes: Record<Species, THREE.InstancedMesh>;
  private readonly animals: Animal[] = [];
  private rng = mulberry32(20260625);
  private nowSec = 0;
  private enabled = true;   // testing toggle: when off, animals are hidden + frozen (no AI, no boar damage)
  // MP animal authority: true = we run the herd AI (single-player, or the elected host). false = follower:
  // AI is off and every animal lerps toward the host's broadcast snapshot so all clients see one herd.
  private authority = true;
  private readonly tmp = new THREE.Matrix4();
  private readonly tmpQ = new THREE.Quaternion();
  private readonly tmpS = new THREE.Vector3();
  private readonly UP = new THREE.Vector3(0, 1, 0);
  onKill: ((meat: ItemId, amount: number, pos: THREE.Vector3) => void) | null = null;
  // AUDIO AS INFORMATION: a faint hoofstep/footstep as an animal walks near the player -- distance-attenuated
  // + panned by main (engine/audio.ts distantFootstep), so you can hear a boar coming before you see it.
  onFootstep: ((species: Species, x: number, z: number) => void) | null = null;

  constructor() {
    const geom: Record<Species, THREE.BufferGeometry> = {
      deer: buildQuadruped(0.9, 0.55, 'deer'), chicken: buildChicken(), boar: buildQuadruped(0.8, 0.5, 'boar'), cow: buildQuadruped(1.1, 0.7, 'cow'),
    };
    const color: Record<Species, number> = { deer: 0x9c6b3f, chicken: 0xe8e2d2, boar: 0x4a3b30, cow: 0x6b4630 };
    this.meshes = {} as Record<Species, THREE.InstancedMesh>;
    for (const sp of Object.keys(geom) as Species[]) {
      const mat = new THREE.MeshLambertMaterial({ color: color[sp], flatShading: true });
      // Animals join the sealed-base interior dim: they are exactly what OLOS exists to hide (a
      // zoomed-out camera scouting movement over the walls), so they dim with the world and only
      // reveal through the gaze-gated LOS cones.
      applyInteriorDark(mat, true); // instanced: the world-XZ varying reads the instanceMatrix
      const m = new THREE.InstancedMesh(geom[sp], mat, ANIMALS.total);
      m.castShadow = true; m.receiveShadow = true; m.count = 0; m.frustumCulled = false;
      this.meshes[sp] = m; this.group.add(m);
    }
    this.spawnAll();
  }

  regenerate(): void { this.animals.length = 0; this.rng = mulberry32(20260625); this.spawnAll(); }

  // SIGHT LOS (owner spec): in third person, an animal the CHARACTER cannot actually see -- any
  // wall, building, ridge or prop between the character's eye and the animal -- fades out of view
  // entirely, so the orbiting camera can never scout what the player couldn't. main provides the
  // blocked() test (the same pooled world boxes bullets collide with, plus a terrain march); pass
  // null in first person (you only see where you look, no tricks). Checks are STAGGERED (each
  // animal re-tests every ~8 frames) and the fade eases so cover edges never pop.
  updateSight(eyeX: number, eyeY: number, eyeZ: number, blocked: ((tx: number, ty: number, tz: number) => boolean) | null, dt: number, frame: number, night = 0): void {
    const ease = Math.min(1, dt * 7);
    // NIGHT: the character can't see far in the dark either -- unlit animals beyond the night sight
    // range fade out even on a clear line. Range eases from unlimited (day, night=0) down to
    // NIGHT.sightRange at deep night (night=1); squared once, compared per animal.
    const nr = night > 0.02 ? NIGHT.sightRange / night : Infinity;
    const nr2 = nr * nr;
    for (let i = 0; i < this.animals.length; i++) {
      const a = this.animals[i];
      if (!a.alive) continue;
      if (blocked === null) a.sightTarget = 1;
      else if ((i & 7) === (frame & 7)) {
        const dx = a.pos.x - eyeX, dy = a.pos.y + 0.55 - eyeY, dz = a.pos.z - eyeZ;
        const d2 = dx * dx + dy * dy + dz * dz;
        // Point-blank (< 3m) always shows: you'd hear/touch it, and door-frame flicker feels broken.
        a.sightTarget = d2 < 9 || (d2 <= nr2 && !blocked(a.pos.x, a.pos.y + 0.55, a.pos.z)) ? 1 : 0;
      }
      a.sightFade += (a.sightTarget - a.sightFade) * ease;
    }
  }

  // Testing toggle: off = hide all animals + skip the per-frame AI (so the boar can't wander up and gore you).
  setEnabled(on: boolean): void { this.enabled = on; this.group.visible = on; }

  // MP authority handoff: the host runs the AI (authority=true); everyone else follows the host's snapshots.
  // Single-player never calls this, so it stays true (full local AI). Idempotent.
  setAuthority(on: boolean): void { this.authority = on; }
  get isAuthority(): boolean { return this.authority; }

  // The index of an animal in the deterministic herd list (same on every client) -- so a follower can name
  // the exact animal it hit when it routes the hit to the host. Returns -1 if not found.
  indexOf(a: Animal): number { return this.animals.indexOf(a); }

  // HOST applies a follower's routed melee hit to animal #index; the kill + meat drop then sync to all.
  damageById(index: number, dmg: number): void { const a = this.animals[index]; if (a) this.damage(a, dmg); }

  // Indices of the live animals within `radius` of (x,z) -- so a MP follower's explosion can route each
  // hit to the host by index instead of killing animals it doesn't own (which the host would then revive).
  indicesInRadius(x: number, z: number, radius: number): number[] {
    const r2 = radius * radius, out: number[] = [];
    for (let i = 0; i < this.animals.length; i++) { const a = this.animals[i]; if (!a.alive) continue; const dx = a.pos.x - x, dz = a.pos.z - z; if (dx * dx + dz * dz <= r2) out.push(i); }
    return out;
  }

  // HOST -> wire: a compact flat snapshot of the herd, index-aligned to the deterministic spawn order.
  // Four numbers per animal: x, z, yaw, alive(1/0). y is re-derived from terrain on the follower, and
  // species is positional, so this is all a follower needs to render + target the same herd.
  netState(): number[] {
    const out = new Array<number>(this.animals.length * 4);
    for (let i = 0; i < this.animals.length; i++) {
      const a = this.animals[i], o = i * 4;
      out[o] = Math.round(a.pos.x * 100) / 100; out[o + 1] = Math.round(a.pos.z * 100) / 100;
      out[o + 2] = Math.round(a.yaw * 1000) / 1000; out[o + 3] = a.alive ? 1 : 0;
    }
    return out;
  }

  // FOLLOWER <- wire: adopt the host's snapshot as lerp targets (movement eases toward them in update()).
  // A live->dead flip just hides the animal; the meat it dropped rides the normal synced drop path (the
  // HOST's onKill), so followers must NOT drop meat here.
  applyNet(states: number[]): void {
    const n = Math.min(this.animals.length, Math.floor(states.length / 4));
    for (let i = 0; i < n; i++) {
      const a = this.animals[i], o = i * 4;
      const wasAlive = a.alive;
      a.netX = states[o]; a.netZ = states[o + 1]; a.netYaw = states[o + 2];
      a.alive = states[o + 3] === 1;
      // A respawn (dead -> alive) teleports to a fresh spot: SNAP there instead of lerping, or the animal
      // would visibly glide across the island from where it died to where the host respawned it.
      if (a.alive && !wasAlive) { a.pos.set(a.netX, terrainHeight(a.netX, a.netZ), a.netZ); a.yaw = a.netYaw; }
    }
  }

  // TEST: drop one of each species in a ring ~15m around the player so all 4 (incl. the charging boar) are
  // right there on spawn. Relocates existing animals -- no extra spawns.
  seedNear(px: number, pz: number): void {
    const want: Species[] = ['deer', 'chicken', 'cow', 'boar'];
    for (let i = 0; i < want.length; i++) {
      const a = this.animals.find((an) => an.species === want[i] && an.alive);
      if (!a) continue;
      const ang = (i / want.length) * Math.PI * 2, r = want[i] === 'boar' ? 18 : 14;
      a.pos.set(px + Math.cos(ang) * r, 0, pz + Math.sin(ang) * r);
      a.pos.y = terrainHeight(a.pos.x, a.pos.z);
      a.vel.set(0, 0, 0);
    }
  }

  private spawnAll(): void {
    const order: Species[] = ['deer', 'deer', 'cow', 'chicken', 'boar']; // rough mix
    for (let i = 0; i < ANIMALS.total; i++) {
      const sp = order[i % order.length];
      const spot = this.findSpot(sp);
      if (!spot) continue;
      const yaw = this.rng() * Math.PI * 2;
      this.animals.push({ species: sp, pos: new THREE.Vector3(spot.x, terrainHeight(spot.x, spot.z), spot.z), vel: new THREE.Vector3(), yaw, hp: SPECIES[sp].hp, alive: true, wanderT: 0, wanderYaw: this.rng() * Math.PI * 2, attackCd: 0, respawnAt: 0, sightFade: 1, sightTarget: 1, strideDist: 0, netX: spot.x, netZ: spot.z, netYaw: yaw, retreatUntil: 0 });
    }
  }

  // A land spot in a biome the species likes. Returns null if none found. Spots are EVEN by area (sqrt -> no
  // centre clumping) and reach the COAST (islandOuter), so herds spread across the whole island incl. the
  // spawn shoreline -- not bunched near the irradiated centre and cut off at islandInner like before.
  private findSpot(sp: Species): { x: number; z: number } | null {
    for (let t = 0; t < 30; t++) {
      const a = this.rng() * Math.PI * 2, d = Math.sqrt(this.rng()) * (TERRAIN.islandOuter - 40);
      const x = Math.cos(a) * d, z = Math.sin(a) * d, h = terrainHeight(x, z);
      if (h <= TERRAIN.seaLevel + 2) continue;
      const b = biomeAt(x, z, h);
      const forestish = b === Biome.Forest || b === Biome.Hills;
      const openish = b === Biome.Grassland || b === Biome.Meadow;
      const ok = (sp === 'deer' || sp === 'boar') ? (forestish || openish) : openish;
      if (ok || this.rng() < 0.2) return { x, z };
    }
    return null;
  }

  // The nearest living animal within `range` of (px,pz) -- for melee aim.
  nearestLive(px: number, pz: number, range: number): Animal | null {
    let best: Animal | null = null, bestD = range * range;
    for (const a of this.animals) { if (!a.alive) continue; const dx = a.pos.x - px, dz = a.pos.z - pz, dd = dx * dx + dz * dz; if (dd < bestD) { bestD = dd; best = a; } }
    return best;
  }

  // AREA damage (explosions): hurt every live animal whose centre is within `radius` of (x,z). Damage scales
  // from full at the blast centre (lethal) down to dmg*edgeFrac at the edge (1 = the old flat behaviour).
  // Returns the count hit.
  damageRadius(x: number, z: number, radius: number, dmg: number, edgeFrac = 1): number {
    const r2 = radius * radius; let n = 0;
    for (const a of [...this.animals]) {
      if (!a.alive) continue;
      const dx = a.pos.x - x, dz = a.pos.z - z, dd = dx * dx + dz * dz;
      if (dd > r2) continue;
      this.damage(a, dmg * (1 - (1 - edgeFrac) * (Math.sqrt(dd) / radius)));
      n++;
    }
    return n;
  }

  // Nearby live animals as aim-assist candidates (centre at body height + a body radius), so the gun's
  // subtle assist helps you connect on a moving animal instead of sailing past into the terrain behind.
  aimCandidates(px: number, pz: number, range: number): Array<{ pos: THREE.Vector3; radius: number }> {
    const out: Array<{ pos: THREE.Vector3; radius: number }> = [];
    const r2 = range * range;
    for (const a of this.animals) {
      if (!a.alive) continue;
      const dx = a.pos.x - px, dz = a.pos.z - pz;
      if (dx * dx + dz * dz > r2) continue;
      const sp = SPECIES[a.species];
      out.push({ pos: new THREE.Vector3(a.pos.x, a.pos.y + 0.6 * sp.scale, a.pos.z), radius: sp.radius * 1.3 });
    }
    return out;
  }

  // Segment-vs-animal-spheres test for bullets. Returns the hit animal + ray param t, or null.
  hitTest(p0: THREE.Vector3, p1: THREE.Vector3): { animal: Animal; t: number } | null {
    let best: Animal | null = null, bestT = 1;
    for (const a of this.animals) {
      if (!a.alive) continue;
      const cx = a.pos.x, cy = a.pos.y + 0.6 * SPECIES[a.species].scale, cz = a.pos.z, r = SPECIES[a.species].radius;
      const t = segSphereT(p0, p1, cx, cy, cz, r);
      if (t >= 0 && t < bestT) { bestT = t; best = a; }
    }
    return best ? { animal: best, t: bestT } : null;
  }

  // Damage an animal; on death drop meat (via onKill) + schedule a respawn. Returns true if it died.
  damage(a: Animal, dmg: number): boolean {
    if (!a.alive) return false;
    a.hp -= dmg;
    if (a.hp > 0) {
      // A hit also spooks it (skittish ones bolt; boar gets angrier -> handled in AI by proximity).
      a.wanderT = 0;
      return false;
    }
    a.alive = false;
    a.respawnAt = this.nowSec + ANIMALS.respawnSec;
    const def = SPECIES[a.species];
    this.onKill?.(def.meat, def.amount, a.pos);
    return true;
  }

  // World save: every animal (the list is small), so herd positions + wounds + respawn debts survive.
  serialize(): SavedAnimal[] {
    return this.animals.map((a) => ({
      sp: a.species,
      x: Math.round(a.pos.x * 100) / 100, z: Math.round(a.pos.z * 100) / 100,
      hp: Math.max(0, Math.round(a.hp)), // overkill drives hp far negative; 0 is all a dead entry needs
      rem: a.alive ? undefined : Math.max(1, a.respawnAt - this.nowSec),
    }));
  }

  // Re-apply on boot (nowSec still ~0 -> `rem` maps straight onto the fresh clock). Matched by index
  // against the deterministic spawn list; a species mismatch means the roster changed, skip that slot.
  deserialize(data: SavedAnimal[]): void {
    for (let i = 0; i < data.length && i < this.animals.length; i++) {
      const s = data[i], a = this.animals[i];
      if (s.sp !== a.species) continue;
      a.pos.set(s.x, terrainHeight(s.x, s.z), s.z);
      a.vel.set(0, 0, 0);
      a.hp = Math.min(SPECIES[a.species].hp, Math.max(1, s.hp));
      a.alive = s.rem === undefined;
      a.respawnAt = s.rem === undefined ? 0 : this.nowSec + s.rem;
      a.attackCd = 0;
    }
  }

  // Solid boxes for the BIG nearby animals (cow/boar) so you cannot walk through them.
  collectColliders(out: Box[], pool: BoxPool, px: number, pz: number, range: number): void {
    const r2 = range * range;
    for (const a of this.animals) {
      if (!a.alive || !SPECIES[a.species].big) continue;
      const dx = a.pos.x - px, dz = a.pos.z - pz; if (dx * dx + dz * dz > r2) continue;
      const s = SPECIES[a.species].scale, hr = 0.45 * s;
      out.push(pool.take(a.pos.x, a.pos.y + 0.5 * s, a.pos.z, hr, 0.6 * s, hr, a.yaw));
    }
  }

  // Per-frame: AI for nearby animals (wander / flee / boar charge+gore), respawns, and redraw all
  // live ones. `night` (0 day .. 1 deep night) shrinks the skittish flee-detect range: animals
  // can't see you in the dark either, so you can stalk much closer before they bolt.
  // hurt(sourceX, sourceZ) reports the goring boar's position, for the HUD's directional hit indicator.
  // `others` returns the nearest OTHER (remote) live player to a point. The host runs the herd sim, so without
  // this it only ever fled from the host -- a second player (the follower) could walk right up and nothing ran.
  // Now each animal reacts to the NEAREST player among the local one and the remotes, and the host's snapshot
  // carries that to everyone, so animals flee from whoever is actually near them.
  update(dt: number, time: number, px: number, pz: number, hurt: (sourceX: number, sourceZ: number) => void, night = 0, others?: (ax: number, az: number) => { x: number; z: number } | null): void {
    if (!this.enabled) return; // testing: animals off -> no AI, no boar damage, nothing drawn
    this.nowSec = time;
    if (!this.authority) { this.updateFollower(dt, px, pz, hurt); return; } // MP follower: no AI, lerp to host snapshots
    const sea = TERRAIN.seaLevel, active2 = ANIMALS.activeRadius * ANIMALS.activeRadius;
    for (const a of this.animals) {
      if (!a.alive) { if (time >= a.respawnAt) this.respawn(a); continue; }
      // Pick who this animal reacts to: the nearest of the local player and the nearest remote player.
      let tx = px, tz = pz;
      if (others) { const o = others(a.pos.x, a.pos.z); if (o && ((a.pos.x - o.x) ** 2 + (a.pos.z - o.z) ** 2) < ((a.pos.x - px) ** 2 + (a.pos.z - pz) ** 2)) { tx = o.x; tz = o.z; } }
      const ddx = a.pos.x - tx, ddz = a.pos.z - tz, dToPlayer = Math.hypot(ddx, ddz);
      if (ddx * ddx + ddz * ddz <= active2) this.tickAI(a, dt, tx, tz, dToPlayer, hurt, night);
      // Integrate + seat on terrain; turn back at the waterline so they stay on land.
      const nx = a.pos.x + a.vel.x * dt, nz = a.pos.z + a.vel.z * dt;
      const stepDist = Math.hypot(nx - a.pos.x, nz - a.pos.z); // distance actually covered this frame (before water-stop below)
      if (terrainHeight(nx, nz) > sea + 1) { a.pos.x = nx; a.pos.z = nz; }
      else { a.vel.x = 0; a.vel.z = 0; a.wanderYaw += 0.6; a.wanderT = Math.min(a.wanderT, 0.15); } // hit water -> stop + turn inland (no spin)
      a.pos.y = terrainHeight(a.pos.x, a.pos.z);
      if (a.vel.lengthSq() > 0.04) a.yaw = Math.atan2(a.vel.x, a.vel.z);
      // AUDIO: a faint hoofstep every STRIDE_M of ground covered, only worth computing within earshot.
      if (dToPlayer <= AUDIO.range.animalFootstep && a.vel.lengthSq() > 0.15) {
        a.strideDist += stepDist;
        const stride = SPECIES[a.species].big ? 1.6 : 0.9;
        if (a.strideDist >= stride) { a.strideDist = 0; this.onFootstep?.(a.species, a.pos.x, a.pos.z); }
      }
      a.vel.multiplyScalar(0.86); // drag
    }
    this.redraw();
  }

  // MP FOLLOWER integration: the host owns the AI, so we just ease each animal toward the host's last
  // snapshot pos/yaw (seated on our own terrain -- y is never sent), keep the local boar-gore contact check
  // so a boar can still hurt US (each client only damages its own player, so no double-hit), then redraw.
  private updateFollower(dt: number, px: number, pz: number, hurt: (sourceX: number, sourceZ: number) => void): void {
    const k = Math.min(1, dt * 10); // snapshot lerp (~6Hz snapshots ease smooth at this gain)
    for (const a of this.animals) {
      a.attackCd = Math.max(0, a.attackCd - dt);
      if (!a.alive) continue;
      a.pos.x += (a.netX - a.pos.x) * k;
      a.pos.z += (a.netZ - a.pos.z) * k;
      a.pos.y = terrainHeight(a.pos.x, a.pos.z);
      let dyaw = a.netYaw - a.yaw; while (dyaw > Math.PI) dyaw -= Math.PI * 2; while (dyaw < -Math.PI) dyaw += Math.PI * 2;
      a.yaw += dyaw * k;
      // Local boar gore: the host's AI can't reach us, so a boar contacting THIS client's player still bites.
      if (SPECIES[a.species].hostile && a.attackCd <= 0) {
        const dx = a.pos.x - px, dz = a.pos.z - pz;
        if (dx * dx + dz * dz < ANIMALS.boarHitRange * ANIMALS.boarHitRange) { hurt(a.pos.x, a.pos.z); a.attackCd = ANIMALS.boarCooldownSec; }
      }
    }
    this.redraw();
  }

  private tickAI(a: Animal, dt: number, px: number, pz: number, dToPlayer: number, hurt: (sourceX: number, sourceZ: number) => void, night: number): void {
    const def = SPECIES[a.species];
    a.attackCd = Math.max(0, a.attackCd - dt);
    if (def.hostile) {
      // BOAR HIT-AND-RUN (owner): while retreating (just gored you), bolt AWAY and do not re-aggro. Otherwise
      // charge when close; on a gore, deal the hit and START a retreat -- so a boar strikes ONCE, breaks off for
      // boarRetreatSec, then can come back, instead of relentlessly pinning you.
      if (this.nowSec < a.retreatUntil) {
        const away = Math.atan2(a.pos.x - px, a.pos.z - pz);
        a.vel.set(Math.sin(away) * ANIMALS.boarRetreatSpeed, 0, Math.cos(away) * ANIMALS.boarRetreatSpeed);
        return;
      }
      if (dToPlayer < ANIMALS.boarAggroRange) {
        const ang = Math.atan2(px - a.pos.x, pz - a.pos.z);
        a.vel.set(Math.sin(ang) * ANIMALS.boarChargeSpeed, 0, Math.cos(ang) * ANIMALS.boarChargeSpeed);
        if (dToPlayer < ANIMALS.boarHitRange && a.attackCd <= 0) { hurt(a.pos.x, a.pos.z); a.attackCd = ANIMALS.boarCooldownSec; a.retreatUntil = this.nowSec + ANIMALS.boarRetreatSec; }
        return;
      }
    } else if (dToPlayer < def.fleeRange * (1 + (NIGHT.fleeScale - 1) * night)) {
      // SKITTISH: bolt directly away from the player.
      const ang = Math.atan2(a.pos.x - px, a.pos.z - pz);
      a.vel.set(Math.sin(ang) * def.flee, 0, Math.cos(ang) * def.flee);
      return;
    }
    // Idle wander: amble toward a slowly-changing heading.
    a.wanderT -= dt;
    if (a.wanderT <= 0) { a.wanderT = 2 + this.rng() * 4; a.wanderYaw += (this.rng() - 0.5) * 2; if (this.rng() < 0.4) a.wanderYaw = this.rng() * Math.PI * 2; }
    const s = def.speed * (this.rng() < 0.5 ? 1 : 0.4);
    a.vel.x += (Math.sin(a.wanderYaw) * s - a.vel.x) * Math.min(1, dt * 2);
    a.vel.z += (Math.cos(a.wanderYaw) * s - a.vel.z) * Math.min(1, dt * 2);
  }

  private respawn(a: Animal): void {
    const spot = this.findSpot(a.species);
    if (!spot) { a.respawnAt = this.nowSec + 10; return; } // try again soon
    a.pos.set(spot.x, terrainHeight(spot.x, spot.z), spot.z);
    a.vel.set(0, 0, 0); a.hp = SPECIES[a.species].hp; a.alive = true; a.attackCd = 0;
  }

  private redraw(): void {
    const n: Record<Species, number> = { deer: 0, chicken: 0, boar: 0, cow: 0 };
    for (const a of this.animals) {
      if (!a.alive) continue;
      const sp = a.species, slot = n[sp]++;
      // Sight fade applies as scale: a blocked animal shrinks out over ~0.15s (instanced meshes
      // can't alpha-fade per instance; at this speed the shrink reads as a soft pop, not a morph).
      const s = SPECIES[sp].scale * (a.sightFade < 0.03 ? 0 : a.sightFade);
      this.tmpQ.setFromAxisAngle(this.UP, a.yaw);
      this.tmp.compose(a.pos, this.tmpQ, this.tmpS.set(s, s, s));
      this.meshes[sp].setMatrixAt(slot, this.tmp);
    }
    // No computeBoundingSphere: these meshes are frustumCulled=false, so the sphere is never read -- computing
    // it every frame over all instances was pure waste (perf audit).
    for (const sp of Object.keys(this.meshes) as Species[]) { const m = this.meshes[sp]; m.count = n[sp]; m.instanceMatrix.needsUpdate = true; }
  }
}

// --- low-poly geometry --------------------------------------------------------------------------------
function buildQuadruped(bodyLen: number, bodyH: number, sp: Species): THREE.BufferGeometry {
  const parts: THREE.BufferGeometry[] = [];
  const body = new THREE.BoxGeometry(0.5, bodyH, bodyLen); body.translate(0, bodyH * 0.9, 0); parts.push(body);
  const head = new THREE.BoxGeometry(0.32, 0.32, 0.36); head.translate(0, bodyH * 1.2, bodyLen * 0.55); parts.push(head);
  const legGeo = (): THREE.BoxGeometry => new THREE.BoxGeometry(0.12, bodyH * 0.9, 0.12);
  const legs = [[0.18, bodyLen * 0.35], [-0.18, bodyLen * 0.35], [0.18, -bodyLen * 0.35], [-0.18, -bodyLen * 0.35]];
  for (const [lx, lz] of legs) { const l = legGeo(); l.translate(lx, bodyH * 0.45, lz); parts.push(l); }
  if (sp === 'deer') { // antlers
    for (const sgn of [-1, 1]) { const an = new THREE.BoxGeometry(0.05, 0.3, 0.05); an.translate(sgn * 0.1, bodyH * 1.45, bodyLen * 0.55); parts.push(an); }
  } else if (sp === 'boar') { // tusks
    for (const sgn of [-1, 1]) { const tu = new THREE.BoxGeometry(0.04, 0.04, 0.16); tu.translate(sgn * 0.08, bodyH * 1.12, bodyLen * 0.7); parts.push(tu); }
  } else if (sp === 'cow') { for (const sgn of [-1, 1]) { const ho = new THREE.BoxGeometry(0.05, 0.12, 0.05); ho.translate(sgn * 0.1, bodyH * 1.35, bodyLen * 0.55); parts.push(ho); } }
  const g = mergeGeometries(parts, false)!; for (const p of parts) p.dispose(); g.computeVertexNormals(); return g;
}
function buildChicken(): THREE.BufferGeometry {
  const parts: THREE.BufferGeometry[] = [];
  const body = new THREE.BoxGeometry(0.3, 0.3, 0.4); body.translate(0, 0.3, 0); parts.push(body);
  const head = new THREE.BoxGeometry(0.18, 0.2, 0.18); head.translate(0, 0.5, 0.18); parts.push(head);
  const beak = new THREE.BoxGeometry(0.07, 0.07, 0.1); beak.translate(0, 0.48, 0.3); parts.push(beak);
  for (const sgn of [-1, 1]) { const leg = new THREE.BoxGeometry(0.05, 0.18, 0.05); leg.translate(sgn * 0.08, 0.09, 0); parts.push(leg); }
  const g = mergeGeometries(parts, false)!; for (const p of parts) p.dispose(); g.computeVertexNormals(); return g;
}

function segSphereT(p0: THREE.Vector3, p1: THREE.Vector3, cx: number, cy: number, cz: number, r: number): number {
  const dx = p1.x - p0.x, dy = p1.y - p0.y, dz = p1.z - p0.z;
  const fx = p0.x - cx, fy = p0.y - cy, fz = p0.z - cz;
  const aa = dx * dx + dy * dy + dz * dz; if (aa < 1e-9) return -1;
  const b = 2 * (fx * dx + fy * dy + fz * dz), c = fx * fx + fy * fy + fz * fz - r * r;
  const disc = b * b - 4 * aa * c; if (disc < 0) return -1;
  const t = (-b - Math.sqrt(disc)) / (2 * aa);
  return t >= 0 && t <= 1 ? t : -1;
}
